import React, { useRef } from 'react';
import {
  Container, Grid, FormControl, InputLabel, Select, MenuItem,
  Button, CircularProgress, SelectChangeEvent, Box, Typography,
  Paper, Stack, Avatar, ListItemAvatar, ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import { ConnectionDetails } from './components/ConnectionDetails';
import { SMSUpdate } from './components/SMSUpdate';
import { PricingCard } from './components/PricingCard';
import UpdateDialog from './components/UpdateDialog';
import { useConnection } from './hooks/useConnection';
import { getRole, getUserName } from '../../Service/Service';
import { PRIMARY } from 'themes/colors';
import DataGridMRT from '../../components/table/DataGrid';

interface IAgent {
  agent: string;
  agent_photo: string;
  totalConnection: number;
  load: number;
  installationComplete: number;
  regulator: number;
  pipe: number;
  paidLight: number;
  nonHpOven: number;
  hpOven: number;
  bplOven: number;
  paidAmount: number;
  remarks: string;
}

interface IAgentListItem {
  label: string;
  value: string;
}

const Connection: React.FC = () => {
  const {
    isLoading, loadingTable, agent, pricing, sms, data, columns,
    open, setOpen, setOpenPrice, handleChangeSMS, sendSms,
    fetchAgentData, fetchSalesHistory, fetchAgentList, setAgent, updateAgentConnection,
  } = useConnection();

  const [agentList, setAgentList] = React.useState<IAgentListItem[]>([]);
  const [isAgentListLoading, setAgentListLoading] = React.useState(false);
  const [selectedAgent, setSelectedAgent] = React.useState('');

  const initialized = useRef(false);

  React.useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      const loadAgents = async () => {
        setAgentListLoading(true);
        const agents = await fetchAgentList();
        setAgentList(agents);
        setAgentListLoading(false);
      };
      loadAgents();
    }
  }, [fetchAgentList]);

  const handleAgentChange = (event: SelectChangeEvent<string>) => {
    setSelectedAgent(event.target.value);
  };

  const handleFetch = async () => {
    await fetchAgentData(selectedAgent);
  };

  const handleViewHistory = async () => {
    await fetchSalesHistory(selectedAgent);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    setAgent((prev: IAgent) => ({ ...prev, [event.target.name]: event.target.value } as IAgent));
  };

  const handleUpdate = async () => {
    await updateAgentConnection(agent);
    setOpen(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 4 }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${PRIMARY.darker} 0%, ${PRIMARY.dark} 50%, ${PRIMARY.main} 100%)`,
          color: 'white',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 3, md: 4 },
          borderRadius: 1,
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
          Sales & Connections
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Manage agent connection details and track performance metrics
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Agent Selector Panel */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Select Agent
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={7} md={5}>
              {getRole() !== 'user' ? (
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Agent Name</InputLabel>
                  <Select
                    value={selectedAgent}
                    onChange={handleAgentChange}
                    label="Agent Name"
                    sx={{ borderRadius: 2 }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          borderRadius: 2,
                          '& .MuiMenuItem-root': {
                            px: 2, py: 1.5,
                            display: 'flex', alignItems: 'center', gap: 1.5,
                          },
                        },
                      },
                    }}
                  >
                    {isAgentListLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Loading agents...
                      </MenuItem>
                    ) : (
                      agentList.map((item: IAgentListItem) => (
                        <MenuItem key={item.value} value={item.value}>
                          <ListItemAvatar sx={{ minWidth: 40 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                              {item.label[0].toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={item.label} sx={{ m: 0 }} />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              ) : (
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.5, borderRadius: 2,
                    border: '1px solid', borderColor: 'primary.200',
                    bgcolor: 'rgba(63, 81, 181, 0.05)',
                  }}
                >
                  <PersonIcon color="primary" />
                  <Typography variant="body1" fontWeight={600} color="primary.main">
                    {getUserName()}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={5} md={7}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  onClick={handleFetch}
                  startIcon={<SearchIcon />}
                  sx={{
                    borderRadius: 2, px: 3, py: 1.2,
                    background: `linear-gradient(135deg, ${PRIMARY.dark}, ${PRIMARY.main})`,
                    boxShadow: `0 4px 12px ${PRIMARY.main}4D`,
                    fontWeight: 600,
                    '&:hover': { background: `linear-gradient(135deg, ${PRIMARY.darker}, ${PRIMARY.dark})` },
                  }}
                >
                  Fetch & Update
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleViewHistory}
                  startIcon={<HistoryIcon />}
                  sx={{
                    borderRadius: 2, px: 3, py: 1.2, fontWeight: 600,
                    borderColor: 'grey.300', color: 'text.secondary',
                    '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'rgba(79,70,229,0.04)' },
                  }}
                >
                  View History
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
            <CircularProgress size={48} thickness={4} />
            <Typography color="text.secondary">Loading agent data...</Typography>
          </Box>
        )}

        {/* Agent Details */}
        {!isLoading && agent && Object.keys(agent).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <ConnectionDetails agent={agent} pricing={pricing} getRole={getRole} setOpen={setOpen} />
          </Box>
        )}

        {/* SMS & Pricing Cards */}
        {!isLoading && (
          <Grid container spacing={3}>
            {(getRole() === 'admin' || getRole() === 'superadmin') && (
              <Grid item xs={12} md={6}>
                <SMSUpdate agent={agent} sms={sms} handleChangeSMS={handleChangeSMS} sendSms={sendSms} />
              </Grid>
            )}
            {getRole() === 'superadmin' && (
              <Grid item xs={12} md={6}>
                <PricingCard pricing={pricing} setOpenPrice={setOpenPrice} />
              </Grid>
            )}
          </Grid>
        )}

        {/* History Table */}
        {data && data.length > 0 && !loadingTable && (
          <Box sx={{ mt: 4 }}>
            <DataGridMRT
              title="Connection History - Jaman HP Gas"
              columns={columns}
              rows={data}
            />
          </Box>
        )}
      </Container>

      <UpdateDialog
        open={open}
        setOpen={setOpen}
        agent={agent}
        connection={agent}
        handleChange={handleChange}
        handleUpdate={handleUpdate}
      />
    </Box>
  );
};

export default Connection;
